import { SearchFilterBar } from '../../components/SearchFilterBar';
import LmsDeleteConfirmModal from '../../components/LmsDeleteConfirmModal';
import LmsDeleteMenuItem from '../../components/LmsDeleteMenuItem';
import { ContentLoadingState } from '../../components/ContentLoadingState';
import FilterDropdownButton from '../../components/FilterDropdownButton';
import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  BookOpen,
  Clock,
  Trophy,
  Upload,
  Download,
  MoreVertical,
  FileText,
  Eye,
  Users,
  CalendarClock, Hourglass,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { lmsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { formatDate } from '../../utils/dateHelpers';

type ModuleStatus = 'draft' | 'published' | 'archived';

interface ContentModule {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  status: ModuleStatus;
  module_type: 'native' | 'scorm';
  points_reward: number;
  pass_score: number;
  estimated_duration_minutes: number;
  /** "Complete within N days"; null = no due date. */
  due_within_days: number | null;
  /** Minimum reading time in seconds; null = none. */
  min_time_seconds: number | null;
  slide_count: number;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

const statusConfig = {
  draft: { label: 'Draft', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
  published: { label: 'Published', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  archived: { label: 'Archived', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
};

export default function LmsContentPage() {
  const navigate = useNavigate();
  const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;
  const [modules, setModules] = useState<ContentModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ModuleStatus | 'all'>('all');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [assignmentCounts, setAssignmentCounts] = useState<Record<string, number>>({});
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importTitle, setImportTitle] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  // Fetch modules
  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await lmsAPI.getContentModules({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        projectId,
      });
      const data = response.data?.data || [];
      const mappedModules = data.map((m: any) => ({
        id: m.id?.toString() || '',
        title: m.title || '',
        description: m.description || null,
        thumbnail_url: m.thumbnail_url || null,
        status: m.status || 'draft',
        module_type: m.module_type || 'native',
        points_reward: m.points_reward || 0,
        pass_score: m.pass_score || 70,
        estimated_duration_minutes: m.estimated_duration_minutes || 0,
        due_within_days: Number(m.due_within_days) > 0 ? Number(m.due_within_days) : null,
        min_time_seconds: Number(m.min_time_seconds) > 0 ? Number(m.min_time_seconds) : null,
        slide_count: parseInt(m.slide_count) || 0,
        created_by: m.created_by || null,
        created_by_name: m.created_by_name || null,
        created_at: m.created_at || new Date().toISOString(),
        updated_at: m.updated_at || new Date().toISOString(),
      }));
      setModules(mappedModules);

      // Fetch assignment counts if we have modules
      if (mappedModules.length > 0) {
        try {
          const moduleIds = mappedModules.map((m: { id: string }) => m.id);
          const countsResponse = await lmsAPI.getAssignmentCounts(moduleIds, projectId);
          const countsData = countsResponse.data?.data || [];
          const countsMap: Record<string, number> = {};
          countsData.forEach((item: { module_id: string; count: number }) => {
            countsMap[item.module_id] = item.count;
          });
          setAssignmentCounts(countsMap);
        } catch (countErr) {
          console.warn('Could not fetch assignment counts:', countErr);
          // Don't fail the whole page if counts fail
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching content modules:', err);
      setError('Failed to load content modules');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, projectId]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  // Filter modules
  const filteredModules = modules.filter((module) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return module.title.toLowerCase().includes(query) || module.description?.toLowerCase().includes(query);
    }
    return true;
  });

  // Handle publish/unpublish
  const handlePublish = async (moduleId: string) => {
    try {
      setActionLoading(true);
      await lmsAPI.publishModule(moduleId, projectId);
      fetchModules();
    } catch (err) {
      console.error('Error publishing module:', err);
      alert('Failed to publish module');
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  const handleUnpublish = async (moduleId: string) => {
    try {
      setActionLoading(true);
      await lmsAPI.unpublishModule(moduleId, projectId);
      fetchModules();
    } catch (err) {
      console.error('Error unpublishing module:', err);
      alert('Failed to unpublish module');
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle SCORM package import
  const handleImportScorm = async () => {
    if (!importFile) {
      setImportError('Please choose a .zip SCORM package');
      return;
    }
    try {
      setImporting(true);
      setImportError(null);
      await lmsAPI.importScormPackage(importFile, importTitle.trim() || undefined, projectId);
      setShowImport(false);
      setImportFile(null);
      setImportTitle('');
      fetchModules();
    } catch (err: any) {
      setImportError(err?.response?.data?.message || err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Handle delete
  const handleDelete = async (moduleId: string) => {
    try {
      setActionLoading(true);
      await lmsAPI.deleteModule(moduleId, projectId);
      setShowDeleteConfirm(null);
      fetchModules();
    } catch (err) {
      console.error('Error deleting module:', err);
      alert('Failed to delete module');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Modules</h1>
          <p className="text-gray-500 mt-1">Create and manage training content for your team</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="rounded-lg border border-primary-200 bg-white px-4 py-2 text-sm font-semibold text-primary-700 shadow-sm transition-colors hover:bg-primary-50"
          >
            <Upload className="w-4 h-4 inline-block mr-2" />
            Import SCORM
          </button>
          <button
            onClick={() => navigate('/lms/content/new')}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
          >
            <Plus className="w-4 h-4 inline-block mr-2" />
            Create Module
          </button>
        </div>
      </div>

      {/* Content Card */}
      <section className="rounded-lg border border-gray-100 bg-white p-6">
        {/* Filters */}
        <SearchFilterBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search modules..."
        >
            {/* Status filter */}
            <div className="relative">
              <FilterDropdownButton
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                label={statusFilter === 'all' ? 'All Status' : statusConfig[statusFilter].label}
              />

              {showStatusDropdown && (
                <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {['all', 'draft', 'published', 'archived'].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status as ModuleStatus | 'all');
                        setShowStatusDropdown(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                    >
                      {status === 'all' ? 'All Status' : statusConfig[status as ModuleStatus].label}
                    </button>
                  ))}
                </div>
              )}
            </div>
        </SearchFilterBar>

        {/* Content */}
        {loading ? (
          <ContentLoadingState message="Loading content modules..." />
        ) : error ? (
          <div className="text-center py-12">
            <BookOpen className="w-8 h-8 mx-auto mb-4 text-red-500" />
            <p className="text-gray-500">{error}</p>
          </div>
        ) : filteredModules.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No content modules yet</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters or search terms.'
                : 'Get started by creating your first training module.'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <button
                onClick={() => navigate('/lms/content/new')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                Create your first module
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredModules.map((module) => {
              const config = statusConfig[module.status];
              return (
                <div key={module.id} className="rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow">
                  {/* Thumbnail */}
                  <div className="aspect-video bg-gradient-to-br from-primary-100 to-purple-100 relative overflow-hidden">
                    {module.thumbnail_url ? (
                      <img
                        src={module.thumbnail_url}
                        alt={module.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl font-bold text-primary-200">
                          {module.title.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    {module.module_type === 'scorm' && (
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                          SCORM
                        </span>
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
                        {config.label}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 relative">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{module.title}</h3>
                        {module.description && (
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                            {module.description}
                          </p>
                        )}
                      </div>

                      {/* Actions dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setActiveDropdown(activeDropdown === module.id ? null : module.id)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>

                        {activeDropdown === module.id && (
                          <div className="absolute right-0 mt-1 w-48 max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                            <button
                              onClick={() => {
                                navigate(`/lms/content/${module.id}`);
                                setActiveDropdown(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                navigate(`/lms/content/${module.id}/preview`);
                                setActiveDropdown(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              Preview
                            </button>
                            <button
                              onClick={() => {
                                navigate(`/lms/content/${module.id}/assignments`);
                                setActiveDropdown(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                            >
                              <Users className="w-4 h-4" />
                              Assign Users
                              {assignmentCounts[module.id] > 0 && (
                                <span className="ml-auto bg-primary-100 text-primary-700 text-xs px-1.5 py-0.5 rounded-full">
                                  {assignmentCounts[module.id]}
                                </span>
                              )}
                            </button>
                            <div className="border-t border-gray-100" />
                            <button
                              onClick={() =>
                                module.status === 'draft'
                                  ? handlePublish(module.id)
                                  : handleUnpublish(module.id)
                              }
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                            >
                              {module.status === 'draft' ? <Upload className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                              {module.status === 'draft' ? 'Publish' : 'Unpublish'}
                            </button>
                            <div className="border-t border-gray-100" />
                            <LmsDeleteMenuItem
                              onClick={() => {
                                setShowDeleteConfirm(module.id);
                                setActiveDropdown(null);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center flex-wrap gap-3 mt-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{module.estimated_duration_minutes} min</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Trophy className="w-4 h-4" />
                        <span>{module.points_reward} pts</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>{module.slide_count} slides</span>
                      </div>
                      {module.due_within_days && (
                        <div
                          className="flex items-center gap-1"
                          title="Counted from the day the module is published, or from an employee's joining date if they join later"
                        >
                          <CalendarClock className="w-4 h-4" />
                          <span>Due in {module.due_within_days} {module.due_within_days === 1 ? 'day' : 'days'}</span>
                        </div>
                      )}
                      {module.min_time_seconds && (
                        <div
                          className="flex items-center gap-1"
                          title="The least time a learner must spend reading before they can complete it"
                        >
                          <Hourglass className="w-4 h-4" />
                          <span>Min. {Math.round((module.min_time_seconds / 60) * 100) / 100} min</span>
                        </div>
                      )}
                      <div
                        className={`flex items-center gap-1 cursor-pointer hover:text-primary-600 transition-colors ${
                          assignmentCounts[module.id] > 0 ? 'text-primary-600 font-medium' : ''
                        }`}
                        onClick={() => navigate(`/lms/content/${module.id}/assignments`)}
                        title="Manage user assignments"
                      >
                        <Users className="w-4 h-4" />
                        <span>{assignmentCounts[module.id] || 0} assigned</span>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-400">
                        {module.created_by_name || 'Unknown'} · {formatDate(module.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Import SCORM Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Import SCORM Package</h3>
            <p className="mt-1 text-sm text-gray-500">
              Upload a SCORM 1.2 or 2004 <span className="font-medium">.zip</span> exported from your authoring tool
              (Articulate, Captivate, iSpring, etc.).
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
                <input
                  type="text"
                  value={importTitle}
                  onChange={(e) => setImportTitle(e.target.value)}
                  placeholder="Defaults to the package title"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SCORM package (.zip)</label>
                <input
                  type="file"
                  accept=".zip,application/zip,application/x-zip-compressed"
                  onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-primary-700"
                />
              </div>
              {importError && <p className="text-sm text-red-600">{importError}</p>}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => {
                  if (!importing) {
                    setShowImport(false);
                    setImportError(null);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleImportScorm}
                disabled={importing || !importFile}
                className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <LmsDeleteConfirmModal
          title="Delete Module"
          message="Are you sure you want to delete this content module? This will also delete all slides within it. This action cannot be undone."
          onCancel={() => setShowDeleteConfirm(null)}
          onConfirm={() => handleDelete(showDeleteConfirm)}
          loading={actionLoading}
        />
      )}

      {/* Click outside to close dropdowns */}
      {(showStatusDropdown || activeDropdown) && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => {
            setShowStatusDropdown(false);
            setActiveDropdown(null);
          }}
        />
      )}
    </div>
  );
}

